import { ConfirmationDialog } from '@app/components/ConfirmationDialog/confirmation-dialog';

import {
  Bullseye,
  Button,
  EmptyState,
  EmptyStateBody,
  EmptyStateIcon,
  EmptyStateVariant,
  OverflowMenu,
  OverflowMenuContent,
  OverflowMenuGroup,
  OverflowMenuItem,
  Spinner,
  Title,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, SearchIcon } from '@patternfly/react-icons';
import { TableComposable, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { API } from 'src/api';
import { ClusterRepresentation, GetClustersError, GetClustersResponse } from 'src/query/deployments';
import {
  DangerBadge,
  DisabledBadge,
  SuccessBadge,
  UnknownBadge,
  WarningBadge,
} from '@app/components/StatusBadge/status-badge';

const columnNames = {
  name: 'Name',
  domain: 'Domain',
  region: 'Region',
  deployments: 'Deployments',
  healthy: 'State',
  action: 'Actions',
};

const isClusterActive = (status: string | undefined) => {
  return status !== undefined && status === 'ACTIVE';
};

type Props = {
  refresh: string;
  clusters: GetClustersResponse;
  isLoading: boolean;
  refetch: () => void;
  error: GetClustersError | null;
  createDeployment: (cluster) => void;
};

export const StatusBadge = (status: ClusterRepresentation['status']) => {
  if (status === undefined) return <UnknownBadge label="provisioning" />;
  if (status === 'ACTIVE') return <SuccessBadge label="active" />;
  if (status === 'BILLING_SETUP') return <WarningBadge label="billing setup" />;
  if (status === 'PENDING_PAYMENT') return <WarningBadge label="pending payment" />;
  if (status === 'PROVISIONING') return <WarningBadge label="provisioning" />;
  if (status === 'SETUP_EXCEPTION') return <WarningBadge label="setup exception" />;
  if (status === 'PENDING_DELETION') return <DangerBadge label="pending deletion" />;
  if (status === 'DISABLED' || status === 'ARCHIVED') return <DisabledBadge label="provisioning" />;
  return null;
};

export const ClustersTable: React.FC<Props> = ({ refresh, clusters, error, isLoading, refetch, createDeployment }) => {
  const { keycloak } = useKeycloak();
  const [deleteCluster, setDeleteCluster] = useState<ClusterRepresentation | null>(null);
  const [isDeletingCluster, setisDeletingCluster] = useState(false);
  //const [metrics, setMetrics] = useState({});

  const deleteACluster = async () => {
    setisDeletingCluster(true);
    toast('Deleting cluster. Please standby as this can take a few moments...');
    try {
      const deletedCluster = await API.Orgs.deleteCluster({ clusterId: deleteCluster?.id });

      if (deletedCluster.ok) {
        toast.success('Cluster deleted.');
        setDeleteCluster(null);
        refetch();
        return;
      } else {
        console.log('[Error] Could not delete cluster.');
      }
    } catch (e) {
      console.log('[Error] Was not able to delete cluster.');
    }
    setisDeletingCluster(false);
    toast.error('Error trying to delete cluster. Please try again.');
  };

  useEffect(() => {
    refetch();
  }, [refresh]);

  return (
    <>
      {deleteCluster && (
        <ConfirmationDialog
          isOpen={!!deleteCluster}
          title={`Delete this cluster: ${deleteCluster.name}`}
          body="Deleting this cluster will remove all data associated with this realm. This action cannot be undone."
          confirmText={
            isDeletingCluster ? (
              <div>
                <Spinner
                  isSVG
                  size="md"
                  aria-label="Contents of the small example"
                  style={{ '--pf-c-spinner--Color': 'white' }}
                />{' '}
                Deleting cluster...
              </div>
            ) : (
              'Delete'
            )
          }
          confirmVariant="danger"
          onModalCancel={() => setDeleteCluster(null)}
          onModalConfirm={() => deleteACluster()}
          disableConfirm={isDeletingCluster}
        />
      )}
      <TableComposable aria-label="Actions table">
        <Thead>
          <Tr>
            <Th>{columnNames.name}</Th>
            <Th>{columnNames.domain}</Th>
            <Th>{columnNames.region}</Th>
            <Th>{columnNames.deployments}</Th>
            <Th>{columnNames.healthy}</Th>
            <Th>{columnNames.action}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {isLoading && (
            <Tr>
              <Td colSpan={6}>
                <Bullseye>
                  <EmptyState>
                    <EmptyStateIcon variant="container" component={Spinner} />
                    <Title size="lg" headingLevel="h2">
                      Loading
                    </Title>
                  </EmptyState>
                </Bullseye>
              </Td>
            </Tr>
          )}
          {error && !isLoading && (
            <Tr>
              <Td colSpan={7}>
                <EmptyState variant={EmptyStateVariant.small}>
                  <EmptyStateIcon icon={ExclamationCircleIcon} />
                  <Title headingLevel="h2" size="lg">
                    No Clusters found
                  </Title>
                  <EmptyStateBody>
                    There was an error retrieving data. Check your connection and reload the page.
                  </EmptyStateBody>
                </EmptyState>
              </Td>
            </Tr>
          )}
          {clusters.length === 0 && !isLoading && !error && (
            <Tr>
              <Td colSpan={6}>
                <EmptyState>
                  <EmptyStateIcon icon={SearchIcon} />
                  <Title headingLevel="h2" size="lg">
                    No Clusters found
                  </Title>
                  <EmptyStateBody>No Clusters are currently created. Create a Cluster above.</EmptyStateBody>
                </EmptyState>
              </Td>
            </Tr>
          )}
          {clusters.length > 0 &&
            !isLoading &&
            !error &&
            clusters.map((cluster) => {
              return (
                <Tr key={cluster.name}>
                  <Td dataLabel={columnNames.name}>
                    <Link to={`/deployments/cluster/${cluster.id}`}>{cluster.name} </Link>
                  </Td>
                  <Td dataLabel={columnNames.domain}>{cluster.domain}</Td>
                  <Td dataLabel={columnNames.region}>{cluster.region}</Td>
                  <Td dataLabel={columnNames.deployments}>{cluster.num_deployments}</Td>
                  <Td dataLabel={columnNames.healthy}>
                    {StatusBadge(cluster.status as ClusterRepresentation['status'])}
                  </Td>
                  <Td>
                    <OverflowMenu breakpoint="md">
                      <OverflowMenuContent>
                        <OverflowMenuGroup groupType="button">
                          <OverflowMenuItem>
                            <Button
                              variant="link"
                              isDisabled={!isClusterActive(cluster.status)}
                              onClick={() => createDeployment(cluster)}
                              style={{ paddingLeft: 0 }}
                            >
                              Create Deployment
                            </Button>
                          </OverflowMenuItem>
                          <OverflowMenuItem>
                            <Button
                              onClick={() => setDeleteCluster(cluster)}
                              variant="link"
                              isDanger
                              title="This will delete the cluster and all data associated."
                            >
                              Delete
                            </Button>
                          </OverflowMenuItem>
                        </OverflowMenuGroup>
                      </OverflowMenuContent>
                    </OverflowMenu>
                  </Td>
                </Tr>
              );
            })}
        </Tbody>
      </TableComposable>
    </>
  );
};
